import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Users, Phone, MessageCircle, AlertTriangle } from "lucide-react";

export default function Customers() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/customers", { params: q ? { q } : {} }); setItems(data); }
    catch (e) { toast.error(apiErr(e)); }
  }, [q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/customers/${id}/history`); setDetail(data); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid="customers-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-primary">Anagrafica</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Clienti</h1>
      </div>
      <div className="relative">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome o telefono..." className="pl-9" data-testid="customer-search-input" />
      </div>

      {items.length === 0 ? <p className="text-slate-400 py-12 text-center">Nessun cliente trovato.</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((c) => (
            <button key={c.id} onClick={() => openDetail(c.id)} className="text-left bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-md transition-shadow" data-testid={`customer-card-${c.id}`}>
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-full bg-primary/10 text-primary font-heading font-bold">{c.name.charAt(0)}</div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{c.name}</p>
                  <p className="text-xs text-slate-500 flex items-center gap-1"><Phone className="h-3 w-3" /> {c.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3 text-xs">
                <span className="flex items-center gap-1 text-slate-600"><Users className="h-3.5 w-3.5" /> {c.total_reservations} pren.</span>
                {c.no_shows > 0 && <span className="flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> {c.no_shows} no-show</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="customer-detail-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{detail.customer.name}</DialogTitle></DialogHeader>
              <div className="flex items-center justify-between rounded-xl bg-muted p-3">
                <div>
                  <p className="text-sm text-slate-600 flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {detail.customer.phone}</p>
                  {detail.customer.email && <p className="text-xs text-slate-500">{detail.customer.email}</p>}
                </div>
                <a href={`https://wa.me/${detail.customer.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 text-white px-3 py-2 text-sm font-medium" data-testid="customer-whatsapp-link">
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
              </div>
              <div className="max-h-72 overflow-y-auto hide-scrollbar space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase">Storico prenotazioni</p>
                {detail.history.map((h) => (
                  <div key={h.id} className="flex items-center justify-between rounded-lg border border-slate-100 p-3 text-sm">
                    <div>
                      <p className="font-medium text-slate-800">{h.checkin ? `${h.checkin} → ${h.checkout}` : `${h.date} · ${h.time}`}</p>
                      <p className="text-xs text-slate-500">{h.party_size} {h.checkin ? "ospiti" : "coperti"}</p>
                    </div>
                    <StatusBadge status={h.status} />
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
